import { pollWhileVisible, type components } from "@roastery/api-client";
import { useApi } from "@roastery/auth";
import { useQueryClient } from "@tanstack/react-query";

export type Notification = components["schemas"]["Notification"];
// springdoc leaves record components optional in the schema, hence the NonNullable wrappers.
export type NotificationType = NonNullable<Notification["type"]>;
export type NotificationSeverity = NonNullable<Notification["severity"]>;
export type NotificationPreference = components["schemas"]["NotificationPreference"];
export type EmailDelivery = NonNullable<NotificationPreference["email"]>;

export const EMAIL_DELIVERIES: readonly EmailDelivery[] = ["OFF", "IMMEDIATE", "DAILY"];

const LIST_PATH = "/api/v1/notifications";
const UNREAD_PATH = "/api/v1/notifications/unread-count";
const PREFERENCES_PATH = "/api/v1/notifications/preferences";

/**
 * How often the badge asks again — and only while somebody is looking at the tab (US-18.14).
 *
 * <p>Two minutes rather than one: the e-mail queue runs every minute, so a minute here bought at best
 * half a minute of freshness for twice the traffic, and this is the request that scales with the
 * number of people signed in rather than with the work they do (INBOX, US-16.3).
 */
const UNREAD_POLL_MS = 120_000;

/** How many rows the bell holds; more than that belongs on a screen, which US-16.3 did not build. */
export const BELL_PAGE_SIZE = 20;

export function useNotifications(unreadOnly: boolean, enabled = true) {
  const api = useApi();
  return api.query.useQuery(
    "get",
    LIST_PATH,
    { params: { query: { unreadOnly, page: 0, size: BELL_PAGE_SIZE } } },
    { enabled },
  );
}

/**
 * The number on the bell. Polled rather than pushed: an alert that appears within a minute is soon
 * enough for a roastery, and a websocket for one integer would be the only one in the application.
 */
export function useUnreadCount() {
  const api = useApi();
  return api.query.useQuery("get", UNREAD_PATH, {}, pollWhileVisible(UNREAD_POLL_MS));
}

/** Both reads change after any of the mutations: the list shows what was read, the badge counts it. */
function useInvalidateInbox() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["get", LIST_PATH] });
    void queryClient.invalidateQueries({ queryKey: ["get", UNREAD_PATH] });
  };
}

export function useMarkRead() {
  const api = useApi();
  const invalidate = useInvalidateInbox();
  return api.query.useMutation("post", "/api/v1/notifications/{id}/read", { onSuccess: invalidate });
}

export function useMarkAllRead() {
  const api = useApi();
  const invalidate = useInvalidateInbox();
  return api.query.useMutation("post", "/api/v1/notifications/read-all", { onSuccess: invalidate });
}

export function usePreferences() {
  const api = useApi();
  return api.query.useQuery("get", PREFERENCES_PATH, {});
}

export function useSavePreferences() {
  const api = useApi();
  const queryClient = useQueryClient();
  return api.query.useMutation("put", PREFERENCES_PATH, {
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["get", PREFERENCES_PATH] }),
  });
}

/**
 * "Send me a test": the answer to "is e-mail configured at all". It writes a notification too, so the
 * bell has to be refreshed like after any other change.
 */
export function useSendTest() {
  const api = useApi();
  const invalidate = useInvalidateInbox();
  return api.query.useMutation("post", "/api/v1/notifications/test", { onSuccess: invalidate });
}

export type AlertState = components["schemas"]["AlertState"];
export type AlertStatus = NonNullable<AlertState["status"]>;

const TODO_PATH = "/api/v1/notifications/todo";

/**
 * "To do today" (US-22.3): the alerts that are a job rather than news, filtered by the caller's roles.
 * On every dashboard, so it polls on the same visibility-aware schedule as the bell — a list that
 * ticks over while nobody is looking is the traffic US-18.14 went and removed.
 */
export function useTodo() {
  const api = useApi();
  return api.query.useQuery("get", TODO_PATH, {}, pollWhileVisible(UNREAD_POLL_MS));
}

/** All three actions change the same list, and two of them also stop a notification arriving. */
function useInvalidateTodo() {
  const queryClient = useQueryClient();
  const invalidateInbox = useInvalidateInbox();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["get", TODO_PATH] });
    invalidateInbox();
  };
}

export function useAcknowledgeAlert() {
  const api = useApi();
  const invalidate = useInvalidateTodo();
  return api.query.useMutation("post", "/api/v1/notifications/todo/{id}/acknowledge", {
    onSuccess: invalidate,
  });
}

export function useSnoozeAlert() {
  const api = useApi();
  const invalidate = useInvalidateTodo();
  return api.query.useMutation("post", "/api/v1/notifications/todo/{id}/snooze", {
    onSuccess: invalidate,
  });
}

export function useResolveAlert() {
  const api = useApi();
  const invalidate = useInvalidateTodo();
  return api.query.useMutation("post", "/api/v1/notifications/todo/{id}/resolve", {
    onSuccess: invalidate,
  });
}
